/**
 * Paid-To-Listen homepage promo strip — "Earn while you listen".
 *
 * Two variants:
 *   • Plenty of slots (>10 left) → friendly "Earn $X / month" pitch.
 *   • Final slots (≤10 left)     → urgent "Only N spots left of 100"
 *                                   bonus pitch.
 *
 * Hidden once all 100 launch bonuses are claimed AND the viewer is
 * already enrolled (avoids nagging existing earners).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, Gift, Sparkles, ArrowRight, X } from 'lucide-react';
import { EarningsTicker } from '../ptl/EarningsTicker';
import { PayoutsCounter } from '../ptl/PayoutsCounter';
import './EarnWhileYouListenPromo.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'ptl_promo_dismissed_at';
const DISMISS_HOURS = 48;

export default function EarnWhileYouListenPromo() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Honour recent dismissals
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (ts && (Date.now() - Number(ts)) < DISMISS_HOURS * 3600_000) {
        setDismissed(true);
        return;
      }
    } catch (_) { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ptl/launch-bonus-public`);
        if (!cancelled) setData(data);
      } catch (_) { /* silently ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setDismissed(true);
  };

  if (dismissed || !data) return null;

  const { remaining, limit, credits_per_winner: cpw, credit_amount_usd: cau, annual_price_usd: ap, monthly_price_usd: mp } = data;
  const slotsLeft = remaining > 0;
  const urgent = slotsLeft && remaining <= 10;
  const totalGift = (cpw || 3) * (cau || 10);

  return (
    <section
      className="relative pt-6"
      data-testid="ptl-home-promo"
    >
      <div
        className={[
          'dark-surface relative overflow-hidden border-y',
          'bg-gradient-to-r from-[#001a0a] via-[#001f15] to-[#0d1f0d]',
          urgent ? 'border-[#FFDA33]/60' : 'border-[#00ff88]/30',
        ].join(' ')}
      >
        {/* Decorative blurs */}
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#00ff88]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#FFDA33]/10 blur-3xl pointer-events-none" />

        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-2 right-2 z-10 p-1.5 text-[#888] hover:text-white"
          aria-label="Dismiss"
          data-testid="ptl-promo-dismiss"
        >
          <X size={14} />
        </button>

        <div className="relative p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.4em] text-[#00ff88]">
              <Sparkles size={11} /> New · Paid To Listen
            </div>
            <h2 className="mt-2 text-xl sm:text-2xl md:text-3xl font-['Chakra_Petch'] font-bold uppercase text-white leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
              Earn while you listen.
            </h2>
            <p className="mt-2 text-[#dddddd] font-mono text-xs sm:text-sm max-w-2xl">
              <span className="text-[#00ff88] font-semibold">$0.003</span> per full play,{' '}
              <span className="text-[#00ff88] font-semibold">+$0.001</span> per like / comment.
              Choose <span className="text-white font-semibold">${mp ?? 11.99}/mo</span> or{' '}
              <span className="text-white font-semibold">${ap}/yr</span> — both ship with{' '}
              <strong className="text-[#FFDA33]">50% off any VIP plan — forever</strong> as a thank-you.
            </p>

            {slotsLeft ? (
              <div
                className={[
                  'mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-mono text-[11px]',
                  urgent
                    ? 'border-[#FFDA33]/60 bg-[#FFDA33]/10 text-[#FFDA33]'
                    : 'border-[#00ff88]/40 bg-[#00ff88]/10 text-[#00ff88]',
                ].join(' ')}
                data-testid="ptl-promo-slots"
              >
                <Gift size={12} />
                {urgent
                  ? <>Only <strong>{remaining}</strong> spots left of {limit} — first {limit} creators also get <strong>${totalGift} in boost credits</strong> (90-day expiry)</>
                  : <>First {limit} creators also get <strong>${totalGift} in boost credits</strong> ({cpw} × ${cau}, expires 90 days)</>
                }
              </div>
            ) : null}

            {/* Live conversion proof: payout total + scrolling ticker.
                Both stay readable on the dark gradient banner. */}
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <PayoutsCounter />
              <div className="flex-1 min-w-0 rounded-lg border border-[#1a1a1a] bg-black/40 backdrop-blur-sm py-1.5 px-3">
                <EarningsTicker compact />
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/paid-to-listen/info')}
              className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[#00ff88]/40 text-[#00ff88] hover:bg-[#00ff88]/10 font-mono uppercase text-[11px] tracking-widest transition-colors"
              data-testid="ptl-promo-info"
            >
              How it works
            </button>
            <button
              type="button"
              onClick={() => navigate('/paid-to-listen')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00ff88] text-black font-mono uppercase text-xs sm:text-sm tracking-widest hover:bg-[#20ffa0] transition-colors"
              data-testid="ptl-promo-cta"
            >
              <DollarSign size={14} />
              Start earning
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
